import { KeyOutlined } from "@ant-design/icons";
import { Modal, Spin, Table, Tag, Timeline, Typography } from "antd";
import { useAppDispatch, useAppSelector } from "hooks";
import React from "react";
import { getSurIdDetailsRequest } from "store/surid/actions";

// PRE-2378 / PRE-558 — golden record identity: the immutable SURID, its permanent
// source XREFs, and the merge/resolution history trail.

interface ISuridDetailModalProps {
    open: boolean;
    onClose: () => void;
    surId?: string | null;
}

export default function SuridDetailModal({ open, onClose, surId }: ISuridDetailModalProps) {
    const dispatch = useAppDispatch();
    const details = useAppSelector((s) => s.surid?.details);
    const loading = useAppSelector((s) => s.surid?.loading);
    const getDetailsResult = useAppSelector((s) => s.surid?.getDetailsResult);

    React.useEffect(() => {
        if (open && surId) {
            dispatch(getSurIdDetailsRequest(surId));
        }
    }, [open, surId, dispatch]);

    const xrefColumns = [
        { title: "Source record", dataIndex: "sourceDataObjectId", ellipsis: true },
        { title: "Source dataset", dataIndex: "sourceDataSetId", ellipsis: true },
        {
            title: "Status",
            dataIndex: "active",
            width: 110,
            render: (active: boolean, xref: { reactivatedDate?: string }) =>
                active ? <Tag color={xref.reactivatedDate ? "gold" : "green"}>{xref.reactivatedDate ? "Reactivated" : "Active"}</Tag> : <Tag>Inactive</Tag>,
        },
        { title: "Linked", dataIndex: "linkedDate", width: 190, render: (v: string) => (v ? new Date(v).toLocaleString() : "") },
    ];

    return (
        <Modal
            title={
                <span className="inline-flex items-center gap-2">
                    <KeyOutlined className="text-purple-500" />
                    Golden record identity
                </span>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            width={720}
            data-testid="surid-detail-modal"
        >
            {loading ? (
                <div className="flex justify-center py-8">
                    <Spin />
                </div>
            ) : !details ? (
                getDetailsResult && !getDetailsResult.success ? (
                    <Typography.Text type="danger">Could not load the identity record — please retry.</Typography.Text>
                ) : (
                    <Typography.Text type="secondary">No identity record found for {surId}.</Typography.Text>
                )
            ) : (
                <div className="flex flex-col gap-4">
                    <div>
                        <Typography.Text strong className="mr-2">
                            SURID
                        </Typography.Text>
                        <Typography.Text code copyable>
                            {details.surId}
                        </Typography.Text>
                        <div className="mt-1 text-xs text-gray-500">
                            Immutable surrogate id, assigned when this record's cluster was first merged · golden record {details.dataObjectId} · created{" "}
                            {details.createdDate ? new Date(details.createdDate).toLocaleString() : "—"}
                        </div>
                    </div>
                    <div>
                        <Typography.Text strong>Source cross-references (XREF — permanent, never deleted)</Typography.Text>
                        <Table
                            className="mt-1"
                            rowKey="sourceDataObjectId"
                            size="small"
                            columns={xrefColumns}
                            dataSource={details.xrefs || []}
                            pagination={false}
                            locale={{ emptyText: "No merged sources yet" }}
                        />
                    </div>
                    <div>
                        <Typography.Text strong>History (PRE-558)</Typography.Text>
                        <Timeline
                            className="mt-3"
                            items={(details.history || [])
                                .slice()
                                .reverse()
                                .map((event: { eventType: string; performedDate: string; performedBy: string; mergedDataObjectIds?: string[]; resolvedFields?: string[]; reason?: string }) => ({
                                    color: event.eventType === "STEWARD_RESOLVED" ? "orange" : "purple",
                                    children: (
                                        <div className="text-xs">
                                            <div className="font-semibold">
                                                {event.eventType === "MERGE_EXECUTED" ? "Merge executed" : event.eventType === "STEWARD_RESOLVED" ? "Steward resolved" : event.eventType}
                                                <span className="ml-2 font-normal text-gray-500">{event.performedDate ? new Date(event.performedDate).toLocaleString() : ""}</span>
                                            </div>
                                            {event.mergedDataObjectIds && event.mergedDataObjectIds.length > 0 && <div>Merged {event.mergedDataObjectIds.length} record(s)</div>}
                                            {event.resolvedFields && event.resolvedFields.length > 0 && <div>Fields: {event.resolvedFields.join(", ")}</div>}
                                            {event.reason && <div className="italic text-gray-500">"{event.reason}"</div>}
                                        </div>
                                    ),
                                }))}
                        />
                        {(details.history || []).length === 0 && <Typography.Text type="secondary">No history yet.</Typography.Text>}
                    </div>
                </div>
            )}
        </Modal>
    );
}
